import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, User, Car, CheckCircle } from "lucide-react";
import { companyInfo } from "../data/mockData";
import { useContact } from "../hooks/useContact";
import LoadingSpinner from "../components/LoadingSpinner";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    vehicleInterest: "",
    message: ""
  });

  const { submitContact, loading, error, success, resetStatus } = useContact();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Reset status when user starts typing
    if (error || success) {
      resetStatus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitted = await submitContact(formData);
    
    if (submitted) {
      // Clear form on success
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        vehicleInterest: "",
        message: ""
      });
      
      // Also send to WhatsApp as backup
      const message = `Hello AD Motors,

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Subject: ${formData.subject}
Vehicle Interest: ${formData.vehicleInterest}

Message: ${formData.message}

Best regards,
${formData.name}`;

      const encodedMessage = encodeURIComponent(message);
      // Don't auto-open WhatsApp, let user choose
      console.log("WhatsApp backup message prepared:", encodedMessage);
    }
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${companyInfo.whatsapp}`, "_blank");
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-64 bg-gradient-to-r from-black via-gray-900 to-royal-red/20 flex items-center">
        <div className="container mx-auto">
          <div className="animate-fadeInUp">
            <h1 className="text-5xl font-bold text-white mb-4">Contact Us</h1>
            <p className="text-xl text-gray-200">
              Ready to find your perfect vehicle? Get in touch with our experts today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="glass-effect p-8 rounded-xl">
              <h2 className="text-3xl font-bold text-white mb-6">Send us a Message</h2>
              
              {/* Success Message */}
              {success && (
                <div className="bg-green-500/20 border border-green-500 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    <p className="text-green-400 font-medium">
                      Thank you! Your message has been sent successfully. We'll get back to you soon.
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 mb-6">
                  <p className="text-red-400 font-medium">
                    {error}. You can also contact us directly via WhatsApp.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 mb-2">Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-royal-red outline-none"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Email Address *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-royal-red outline-none"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 mb-2">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-royal-red outline-none"
                        placeholder="Your phone number"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Subject</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-royal-red outline-none"
                    >
                      <option value="">Select a subject</option>
                      <option value="Vehicle Inquiry">Vehicle Inquiry</option>
                      <option value="Import/Export Service">Import/Export Service</option>
                      <option value="Auction Bidding">Auction Bidding</option>
                      <option value="Shipping & Logistics">Shipping & Logistics</option>
                      <option value="General Inquiry">General Inquiry</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Vehicle Interest</label>
                  <div className="relative">
                    <Car className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="vehicleInterest"
                      value={formData.vehicleInterest}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-royal-red outline-none"
                      placeholder="e.g. BMW M3, Lexus LFA, Toyota Supra"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-royal-red outline-none resize-none"
                    placeholder="Tell us about your requirements, budget, timeline, or any specific questions..."
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={loading}
                  className="btn-primary w-full justify-center text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Contact Details */}
              <div className="glass-effect p-8 rounded-xl">
                <h3 className="text-2xl font-bold text-white mb-6">Get in Touch</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-royal-red p-3 rounded-lg">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Phone Numbers</h4>
                      <p className="text-gray-300">{companyInfo.landline}</p>
                      <p className="text-gray-300">{companyInfo.mobile}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-royal-red p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Email Address</h4>
                      <p className="text-gray-300">{companyInfo.email}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-royal-red p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Address</h4>
                      <p className="text-gray-300">{companyInfo.address}</p>
                      <p className="text-sm text-gray-400">Postal Code: {companyInfo.postalCode}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-royal-red p-3 rounded-lg">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Business Hours</h4>
                      <p className="text-gray-300">{companyInfo.businessHours}</p>
                      <p className="text-sm text-gray-400">Japan Standard Time (JST)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp Quick Contact */}
              <div className="glass-effect p-8 rounded-xl">
                <h3 className="text-2xl font-bold text-white mb-4">Quick Contact</h3>
                <p className="text-gray-300 mb-6">
                  Get instant responses to your queries through WhatsApp. Our team is ready to assist you.
                </p>
                <button 
                  onClick={handleWhatsAppClick}
                  className="btn-primary w-full justify-center text-lg py-4"
                >
                  <MessageCircle className="w-5 h-5" />
                  Start WhatsApp Chat
                </button>
              </div>

              {/* Map Placeholder */}
              <div className="glass-effect p-4 rounded-xl">
                <h3 className="text-white font-semibold mb-4">Our Location</h3>
                <div className="bg-gray-800 rounded-lg h-64 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 text-royal-red mx-auto mb-2" />
                    <p className="text-gray-400">Google Maps Integration</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Location: {companyInfo.postalCode}, Japan
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;